/**
 * Stores the user login.
 *
 * File: \src\stores\LoginStore.ts
 * Created Date: Monday, June 3rd 2024, 08:10:18
 * Author: Herrmann Sascha (LDC)
 * -----
 * Last Modified: April 9th 2025, 11:02:16
 * Modified By: Lazarova Vanya (HAU-DCE)
 * -----
 * Copyright (c) 2024-2024 Liebherr-Digital Development Center GmbH
 */
import { useApiStore } from '@/stores/ApiStore';

import { defineStore } from 'pinia';
import { User, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';

import router, { homeRoute } from '@/router';

export interface LoginState {
  userManager: UserManager;
  user: User;
  loginRequired: boolean;
}

export const renewalGap: number = 61 * 1000;

export const useLoginStore = defineStore('loginStore', {
  state: (): LoginState => ({
    userManager: undefined,
    user: undefined,
    loginRequired: false
  }),

  getters: {
    /**
     * Gets the current bearer token.
     *
     * @return {*}  {(() => string | undefined)} The function that returns the bearer token.
     */
    getBearerToken(): () => string | undefined {
      return (): string | undefined => {
        let token: string;

        if (this.user) {
          token = this.user.access_token;
        }

        return token;
      };
    },

    /**
     * Getter for the authentication state. This is just a convenience shortcut to the user manager.
     *
     * @returns {boolean} true if the user is authenticated.
     */
    isAuthenticated(): boolean {
      if (this.user) {
        return true;
      }

      return false;
    },

    /**
     * Gives you the bearer token for requests.
     *
     * @return {*}  {(string | null)} The token prefixed with "Bearer" or null.
     */
    bearerToken(): () => string | undefined {
      return (): string | undefined => {
        let token: string;

        if (this.user) {
          token = this.user.access_token;
        }

        return token;
      };
    },

    /**
     * Gives you the access token for requests.
     *
     * @return {*}  {(string | null)} The token prefixed with "Bearer" or null.
     */
    accessToken(): () => string | null {
      return (): string | null => {
        if (this.isAuthenticated) {
          return this.user.access_token;
        }

        return null;
      };
    },

    /**
     * Gets the preferred user name (which is the email) from the user's profile.
     *
     * @return {*}  {string} A string containing the current user email.
     */
    userEmail(): string {
      let name: string = '?.?@unknown';

      if (this.user) {
        name = this.user.profile.preferred_username;
      }

      return name;
    },

    /**
     * Gets the user name from the user's profile.
     *
     * @return {*}  {string} A string containing just initials.
     */
    userName(): string {
      let name: string = 'unknown';

      if (this.user) {
        name = this.user.profile.name;
      }

      return name;
    },

    needsLogin(): boolean {
      return this.loginRequired;
    }
  },

  actions: {
    async checkStoredLogin(): Promise<boolean> {
      const apiStore = useApiStore();

      this.initUserManager();

      this.userManager.clearStaleState();

      const lifetime = await this.getRemainingTokenLifetime();

      // Check if a stored login has enough lifetime for reuse
      const useStoredLogin: boolean = lifetime - renewalGap > 0;

      if (!useStoredLogin) {
        await this.clearAllKeys();

        this.loginRequired = true;

        return false;
      }

      const authenticatedUser: User = await this.userManager.signinSilent();
      if (!authenticatedUser) {
        this.clearAllKeys();
        this.loginRequired = true;

        return false;
      }

      this.user = authenticatedUser;
      this.userManager.startSilentRenew();

      apiStore.setContext(this.user.access_token);
      this.loginRequired = false;
      return true;
    },

    async getRemainingTokenLifetime(): Promise<number> {
      if (this.userManager) {
        // This is re-login when the user has left the site and comes back:
        const userStore = this.userManager.settings.userStore;

        const keys: string[] = await userStore.getAllKeys();

        if (keys && keys.length === 1) {
          const storedLogin = await userStore.get(keys[0]);
          const storedLoginAsJson = JSON.parse(storedLogin);

          // We take off 60 seconds to be on the safe side
          const storedExpirationDate: Date = new Date(storedLoginAsJson.expires_at * 1000);
          const now: Date = new Date();
          const lifetime: number = storedExpirationDate.getTime() - now.getTime();

          return lifetime;
        }
      }

      return 0;
    },

    /**
     * Clears all keys from the user store.
     *
     * @return {*}  {Promise<void>}
     */
    async clearAllKeys(): Promise<void> {
      if (this.userManager) {
        const userStore = this.userManager.settings.userStore;
        await userStore.getAllKeys().then((keys: string[]): void => {
          keys.forEach((key: string) => userStore.remove(key));
        });
      }
    },

    /**
     * Called to start the login. We just let the user manager do the redirect to the Liebherr login page.
     *
     */
    loginStart(): void {
      this.initUserManager();
      const userManager = this.userManager;

      userManager.signinRedirect();
    },

    /**
     * Called later to finish the user login.
     *
     */
    loginResult(): void {
      this.initUserManager();
      const userManager = this.userManager;

      // This call finishes the login of the user and fills the user object.
      userManager.signinRedirectCallback().then((): void => {
        userManager
          .getUser()
          .then((user) => {
            if (user) {
              this.user = user;
              this.userManager.startSilentRenew();
              router.replace(homeRoute);
            }
          })
          .catch(() => {
            this.loginStart();
          });
      });
    },

    // Logs out the current user.
    logoutStart(): void {
      this.initUserManager();
      const userManager = this.userManager;

      userManager.signoutRedirect();
    },

    /**
     * Initialize user manager for the OIDC provider. Called on every loginStart() and on every refresh of the app.
     *
     */
    initUserManager() {
      if (!this.userManager) {
        const store: WebStorageStateStore = new WebStorageStateStore({
          prefix: 'procon-login.',
          store: window.localStorage
        });

        const config: UserManagerSettings = {
          authority: window.RUNTIME_CONFIG.VITE_LOGIN_AUTHORITY,
          client_id: window.RUNTIME_CONFIG.VITE_LOGIN_CLIENT_ID,
          metadataUrl: window.RUNTIME_CONFIG.VITE_LOGIN_METADATA_URI,
          redirect_uri: window.RUNTIME_CONFIG.VITE_LOGIN_REDIRECT_URI,
          response_type: window.RUNTIME_CONFIG.VITE_LOGIN_RESPONSE_TYPE,
          scope: window.RUNTIME_CONFIG.VITE_LOGIN_SCOPE,
          post_logout_redirect_uri: window.RUNTIME_CONFIG.VITE_BASE_URI,
          silent_redirect_uri: window.RUNTIME_CONFIG.VITE_LOGIN_SILENT_REDIRECT_URI,
          automaticSilentRenew: true,
          userStore: store
        };

        this.userManager = new UserManager(config);

        this.userManager.events.addAccessTokenExpiring(async () => {
          console.log('Token expiring');
          await this.userManager
            .signinSilent()
            .then(async (user: User) => {
              this.user = user;
              console.log('Token renewed', this.user);
            })
            .catch((error) => {
              console.error('Could not renew the token: ' + error);

              this.clearAllKeys();

              router.replace(homeRoute);
            });
        });

        this.userManager.events.addAccessTokenExpired(async () => {
          console.log('Token expired');
        });
      }
    }
  }
});
